import React, { useState, useEffect } from 'react';

// Game of the Amazons Component
// Two players each have 4 amazons on a 10×10 board.
// On each turn a player moves one amazon (queen-like) then shoots an arrow
// (also queen-like) from its new position.  Arrow squares are permanently
// blocked.  A player loses when they have no valid move on their turn.

const BOARD_SIZE = 10;

// Initial board: null | 'white' | 'black' | 'arrow'
const createInitialBoard = () => {
	const board = Array(BOARD_SIZE * BOARD_SIZE).fill(null);
	// White amazons: d1(9,3), g1(9,6), a4(6,0), j4(6,9)
	board[9 * BOARD_SIZE + 3] = 'white';
	board[9 * BOARD_SIZE + 6] = 'white';
	board[6 * BOARD_SIZE + 0] = 'white';
	board[6 * BOARD_SIZE + 9] = 'white';
	// Black amazons: d10(0,3), g10(0,6), a7(3,0), j7(3,9)
	board[0 * BOARD_SIZE + 3] = 'black';
	board[0 * BOARD_SIZE + 6] = 'black';
	board[3 * BOARD_SIZE + 0] = 'black';
	board[3 * BOARD_SIZE + 9] = 'black';
	return board;
};

/**
 * Returns all squares reachable from `from` via queen-like movement on `board`.
 * Treats `board[from]` as the occupant (not blocking outward rays).
 */
const getReachable = (from, board) => {
	const row = Math.floor(from / BOARD_SIZE);
	const col = from % BOARD_SIZE;
	const dirs = [
		[-1, -1], [-1, 0], [-1, 1],
		[ 0, -1],          [ 0, 1],
		[ 1, -1], [ 1, 0], [ 1, 1],
	];
	const reachable = [];
	for (const [dr, dc] of dirs) {
		let r = row + dr;
		let c = col + dc;
		while (r >= 0 && r < BOARD_SIZE && c >= 0 && c < BOARD_SIZE) {
			const idx = r * BOARD_SIZE + c;
			if (board[idx] !== null) break;
			reachable.push(idx);
			r += dr;
			c += dc;
		}
	}
	return reachable;
};

/**
 * Returns true if `player` has at least one valid move on `board`.
 */
const hasValidMoves = (player, board) => {
	for (let i = 0; i < board.length; i++) {
		if (board[i] === player && getReachable(i, board).length > 0) {
			return true;
		}
	}
	return false;
};

/**
 * Collect all (amazonIdx, destIdx, arrowIdx) triples for `player` on `board`.
 */
const getAllMoves = (player, board) => {
	const moves = [];
	for (let amazon = 0; amazon < board.length; amazon++) {
		if (board[amazon] !== player) continue;
		const dests = getReachable(amazon, board);
		for (const dest of dests) {
			// Temporarily apply the amazon move
			const tmpBoard = [...board];
			tmpBoard[amazon] = null;
			tmpBoard[dest] = player;
			const arrows = getReachable(dest, tmpBoard);
			for (const arrow of arrows) {
				moves.push({ amazon, dest, arrow });
			}
		}
	}
	return moves;
};

// ── Component ─────────────────────────────────────────────────────────────────

const Amazons = () => {
	const [gameStarted, setGameStarted] = useState(false);
	const [playerTypes, setPlayerTypes] = useState({ white: 'human', black: 'human' });
	const [board, setBoard] = useState(createInitialBoard);
	const [currentPlayer, setCurrentPlayer] = useState('white');
	const [phase, setPhase] = useState('move'); // 'move' | 'shoot'
	const [selectedAmazon, setSelectedAmazon] = useState(null); // index
	const [movedAmazon, setMovedAmazon] = useState(null);       // new position after move
	const [winner, setWinner] = useState(null);

	// ── Helpers ────────────────────────────────────────────────────────────────

	const resetGame = () => {
		setBoard(createInitialBoard());
		setCurrentPlayer('white');
		setPhase('move');
		setSelectedAmazon(null);
		setMovedAmazon(null);
		setWinner(null);
		setGameStarted(false);
	};

	const startGame = () => setGameStarted(true);

	const handlePlayerTypeChange = (player, type) => {
		setPlayerTypes(prev => ({ ...prev, [player]: type }));
	};

	// ── Human click handler ────────────────────────────────────────────────────

	const handleClick = (idx) => {
		if (winner || !gameStarted) return;
		if (playerTypes[currentPlayer] !== 'human') return;

		if (phase === 'move') {
			if (selectedAmazon === null) {
				// Select an amazon belonging to current player
				if (board[idx] === currentPlayer && getReachable(idx, board).length > 0) {
					setSelectedAmazon(idx);
				}
			} else if (idx === selectedAmazon) {
				// Deselect
				setSelectedAmazon(null);
			} else {
				const validDests = getReachable(selectedAmazon, board);
				if (validDests.includes(idx)) {
					// Move the amazon
					const newBoard = [...board];
					newBoard[selectedAmazon] = null;
					newBoard[idx] = currentPlayer;
					setBoard(newBoard);
					setMovedAmazon(idx);
					setSelectedAmazon(null);
					setPhase('shoot');
				} else if (board[idx] === currentPlayer) {
					// Select a different amazon
					if (getReachable(idx, board).length > 0) {
						setSelectedAmazon(idx);
					}
				}
			}
		} else {
			// phase === 'shoot'
			const validArrows = getReachable(movedAmazon, board);
			if (validArrows.includes(idx)) {
				const newBoard = [...board];
				newBoard[idx] = 'arrow';
				setBoard(newBoard);

				const nextPlayer = currentPlayer === 'white' ? 'black' : 'white';
				if (!hasValidMoves(nextPlayer, newBoard)) {
					setWinner(currentPlayer);
				} else {
					setCurrentPlayer(nextPlayer);
					setPhase('move');
					setMovedAmazon(null);
				}
			}
		}
	};

	// ── Computer player ────────────────────────────────────────────────────────

	useEffect(() => {
		if (!gameStarted || winner) return;
		if (playerTypes[currentPlayer] !== 'computer') return;

		const timeoutId = setTimeout(() => {
			const moves = getAllMoves(currentPlayer, board);
			if (moves.length === 0) {
				const nextPlayer = currentPlayer === 'white' ? 'black' : 'white';
				setWinner(nextPlayer);
				return;
			}
			const { amazon, dest, arrow } = moves[Math.floor(Math.random() * moves.length)];
			const newBoard = [...board];
			newBoard[amazon] = null;
			newBoard[dest] = currentPlayer;
			newBoard[arrow] = 'arrow';
			setBoard(newBoard);

			const nextPlayer = currentPlayer === 'white' ? 'black' : 'white';
			if (!hasValidMoves(nextPlayer, newBoard)) {
				setWinner(currentPlayer);
			} else {
				setCurrentPlayer(nextPlayer);
				setPhase('move');
				setMovedAmazon(null);
			}
		}, 500);

		return () => clearTimeout(timeoutId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPlayer, gameStarted, winner, board, playerTypes]);

	// ── Status message ─────────────────────────────────────────────────────────

	const getStatus = () => {
		if (winner) {
			const name = winner === 'white' ? 'White' : 'Black';
			return `Winner: ${name}!`;
		}
		const name = currentPlayer === 'white' ? 'White' : 'Black';
		if (phase === 'move') return `Current Turn: ${name} — Select and move an amazon`;
		return `Current Turn: ${name} — Shoot an arrow`;
	};

	// ── Configuration screen ───────────────────────────────────────────────────

	if (!gameStarted) {
		return (
			<div className="game-container">
				<h1 className="game-title">Amazons</h1>
				<h2 className="config-subtitle">Player Configuration</h2>
				<div className="player-config">
					<div className="player-config-item">
						<h3>White</h3>
						<div className="player-type-selector">
							<button
								className={`player-type-button ${playerTypes.white === 'human' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange('white', 'human')}
							>
								Human
							</button>
							<button
								className={`player-type-button ${playerTypes.white === 'computer' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange('white', 'computer')}
							>
								Computer
							</button>
						</div>
					</div>
					<div className="player-config-item">
						<h3>Black</h3>
						<div className="player-type-selector">
							<button
								className={`player-type-button ${playerTypes.black === 'human' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange('black', 'human')}
							>
								Human
							</button>
							<button
								className={`player-type-button ${playerTypes.black === 'computer' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange('black', 'computer')}
							>
								Computer
							</button>
						</div>
					</div>
				</div>
				<button className="new-game-button" onClick={startGame}>
					Start Game
				</button>
			</div>
		);
	}

	// ── Compute highlights ─────────────────────────────────────────────────────

	const isHumanTurn = playerTypes[currentPlayer] === 'human';

	let validSet = new Set();
	if (!winner && isHumanTurn) {
		if (phase === 'move' && selectedAmazon !== null) {
			getReachable(selectedAmazon, board).forEach(i => validSet.add(i));
		} else if (phase === 'shoot' && movedAmazon !== null) {
			getReachable(movedAmazon, board).forEach(i => validSet.add(i));
		}
	}

	// ── Board rendering ────────────────────────────────────────────────────────

	const CELL = 100; // SVG units per cell
	const VIEWBOX = BOARD_SIZE * CELL;

	return (
		<div className="game-container">
			<h1 className="game-title">Amazons</h1>
			<div className={`game-status ${winner ? 'winner' : ''}`}>
				{getStatus()}
			</div>
			<svg
				viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
				className="amazons-svg"
			>
				{board.map((cell, idx) => {
					const row = Math.floor(idx / BOARD_SIZE);
					const col = idx % BOARD_SIZE;
					const x = col * CELL;
					const y = row * CELL;

					const isLight = (row + col) % 2 === 0;
					const isSelected = idx === selectedAmazon;
					const isMovedAmazon = idx === movedAmazon;
					const isValid = validSet.has(idx);

					let squareFill;
					if (isSelected || isMovedAmazon) {
						squareFill = '#86efac'; // green highlight
					} else if (isValid) {
						squareFill = '#fde68a'; // yellow hint
					} else if (isLight) {
						squareFill = '#f0d9b5';
					} else {
						squareFill = '#b58863';
					}

					const isClickable = !winner && isHumanTurn && (
						(phase === 'move' && (
							(selectedAmazon === null && cell === currentPlayer) ||
							(selectedAmazon !== null && (isValid || (cell === currentPlayer && idx !== selectedAmazon)))
						)) ||
						(phase === 'shoot' && isValid)
					);

					return (
						<g
							key={idx}
							onClick={() => handleClick(idx)}
							style={{ cursor: isClickable ? 'pointer' : 'default' }}
						>
							{/* Square */}
							<rect
								x={x}
								y={y}
								width={CELL}
								height={CELL}
								fill={squareFill}
								stroke={isSelected || isMovedAmazon ? '#16a34a' : '#8b7355'}
								strokeWidth={isSelected || isMovedAmazon ? 3 : 0.5}
							/>

							{/* Arrow square marker */}
							{cell === 'arrow' && (
								<rect
									x={x + 25}
									y={y + 25}
									width={50}
									height={50}
									rx={4}
									fill="#44403c"
								/>
							)}

							{/* Amazon piece */}
							{(cell === 'white' || cell === 'black') && (
								<>
									<circle
										cx={x + CELL / 2}
										cy={y + CELL / 2}
										r={36}
										fill={cell === 'white' ? '#f8fafc' : '#1e293b'}
										stroke={cell === 'white' ? '#64748b' : '#94a3b8'}
										strokeWidth={3}
									/>
									<text
										x={x + CELL / 2}
										y={y + CELL / 2 + 9}
										textAnchor="middle"
										fontSize="30"
										fontWeight="bold"
										fill={cell === 'white' ? '#334155' : '#e2e8f0'}
									>
										♛
									</text>
								</>
							)}

							{/* Valid-move dot (empty squares) */}
							{isValid && cell === null && (
								<circle
									cx={x + CELL / 2}
									cy={y + CELL / 2}
									r={10}
									fill="rgba(0,0,0,0.2)"
								/>
							)}
						</g>
					);
				})}
			</svg>
			<button className="new-game-button" onClick={resetGame}>
				New Game
			</button>
		</div>
	);
};

export default Amazons;
